import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  MessageSquare, Calendar, FileText, Users, Settings,
  Database, Briefcase, HelpCircle, BookOpen, Target,
  Mail, Phone, Building2, ArrowRight, Globe
} from 'lucide-react';

const translations = {
  es: {
    title: 'Experiencia Virtual de Empleo en Empresa Digital',
    subtitle: 'Experimenta un entorno laboral real con sistemas y herramientas empresariales',
    sections: {
      business: {
        title: 'Sistemas Empresariales',
        items: {
          crm: {
            name: 'Sistema CRM',
            description: 'Gestiona relaciones con clientes y pipeline de ventas'
          },
          erp: {
            name: 'Sistema ERP',
            description: 'Planificación y gestión de recursos empresariales'
          },
          projectManagement: {
            name: 'Gestión de Proyectos',
            description: 'Seguimiento y gestión de proyectos y tareas'
          }
        }
      },
      support: {
        title: 'Soporte y Comunicación',
        items: {
          support: {
            name: 'Sistema de Soporte',
            description: 'Soporte IT y tickets de ayuda'
          },
          collaboration: {
            name: 'Colaboración de Equipo',
            description: 'Chat, reuniones y compartición de archivos'
          },
          hr: {
            name: 'Portal de RRHH',
            description: 'Recursos y gestión de empleados'
          }
        }
      },
      learning: {
        title: 'Aprendizaje y Desarrollo',
        items: {
          training: {
            name: 'Portal de Formación',
            description: 'Accede a materiales y cursos de formación'
          },
          aiTasks: {
            name: 'Tareas IA',
            description: 'Recibe tareas del mundo real asignadas por IA'
          },
          decisions: {
            name: 'Simulación de Decisiones',
            description: 'Practica la toma de decisiones empresariales'
          }
        }
      }
    },
    actions: {
      access: 'Acceder al Sistema'
    }
  },
  en: {
    title: 'Digital Company & Virtual Employment Experience',
    subtitle: 'Experience a real work environment with enterprise systems and tools',
    sections: {
      business: {
        title: 'Business Systems',
        items: {
          crm: {
            name: 'CRM System',
            description: 'Manage customer relationships and sales pipeline'
          },
          erp: {
            name: 'ERP System',
            description: 'Enterprise resource planning and management'
          },
          projectManagement: {
            name: 'Project Management',
            description: 'Track and manage projects and tasks'
          }
        }
      },
      support: {
        title: 'Support & Communication',
        items: {
          support: {
            name: 'Support System',
            description: 'IT support and help desk tickets'
          },
          collaboration: {
            name: 'Team Collaboration',
            description: 'Chat, meetings, and file sharing'
          },
          hr: {
            name: 'HR Portal',
            description: 'Employee resources and management'
          }
        }
      },
      learning: {
        title: 'Learning & Development',
        items: {
          training: {
            name: 'Training Portal',
            description: 'Access training materials and courses'
          },
          aiTasks: {
            name: 'AI Tasks',
            description: 'Get real-world tasks assigned by AI'
          },
          decisions: {
            name: 'Decision Simulation',
            description: 'Practice business decision-making'
          }
        }
      }
    },
    actions: {
      access: 'Access System'
    }
  }
};

const PortalOverview = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const systems = [
    {
      title: t.sections.business.title,
      items: [
        {
          name: t.sections.business.items.crm.name,
          description: t.sections.business.items.crm.description,
          icon: <Users className="h-6 w-6 text-accent" />,
          path: '/dcvee/systems/crm'
        },
        {
          name: t.sections.business.items.erp.name,
          description: t.sections.business.items.erp.description,
          icon: <Database className="h-6 w-6 text-accent" />,
          path: '/dcvee/systems/erp'
        },
        {
          name: t.sections.business.items.projectManagement.name,
          description: t.sections.business.items.projectManagement.description,
          icon: <Target className="h-6 w-6 text-accent" />,
          path: '/dcvee/systems/project-management'
        }
      ]
    },
    {
      title: t.sections.support.title,
      items: [
        {
          name: t.sections.support.items.support.name,
          description: t.sections.support.items.support.description,
          icon: <HelpCircle className="h-6 w-6 text-accent" />,
          path: '/dcvee/systems/support'
        },
        {
          name: t.sections.support.items.collaboration.name,
          description: t.sections.support.items.collaboration.description,
          icon: <MessageSquare className="h-6 w-6 text-accent" />,
          path: '/dcvee/systems/collaboration'
        },
        {
          name: t.sections.support.items.hr.name,
          description: t.sections.support.items.hr.description,
          icon: <Briefcase className="h-6 w-6 text-accent" />,
          path: '/dcvee/systems/hr'
        }
      ]
    },
    {
      title: t.sections.learning.title,
      items: [
        {
          name: t.sections.learning.items.training.name,
          description: t.sections.learning.items.training.description,
          icon: <BookOpen className="h-6 w-6 text-accent" />,
          path: '/dcvee/training'
        },
        {
          name: t.sections.learning.items.aiTasks.name,
          description: t.sections.learning.items.aiTasks.description,
          icon: <Target className="h-6 w-6 text-accent" />,
          path: '/dcvee/systems/ai-tasks'
        },
        {
          name: t.sections.learning.items.decisions.name,
          description: t.sections.learning.items.decisions.description,
          icon: <Settings className="h-6 w-6 text-accent" />,
          path: '/dcvee/systems/decisions'
        }
      ]
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-6 text-white mb-8">
        <h1 className="text-2xl font-bold">{t.title}</h1>
        <p className="mt-2 text-lg text-white/90">{t.subtitle}</p>
      </div>

      <div className="space-y-12">
        {systems.map((section, index) => (
          <div key={index}>
            <h2 className="text-xl font-bold text-foreground mb-6">{section.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {section.items.map((item, itemIndex) => (
                <Link
                  key={itemIndex}
                  to={item.path}
                  className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      {item.icon}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{item.name}</h3>
                      <p className="mt-1 text-foreground/70">{item.description}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <span className="inline-flex items-center text-accent">
                      {t.actions.access}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PortalOverview;